import { createSelector } from '@reduxjs/toolkit';
import { DATA_SLICE_KEY } from './dataSlice';
import { INTERACTION_SLICE_KEY } from './interactionSlice';
import { normalizeBySitePlanId } from "../services/processSitePlans";

const selectMapDataState = (state) => state?.[DATA_SLICE_KEY];
const selectMapInteractionState = (state) => state?.[INTERACTION_SLICE_KEY];

/**
 * It returns the results from the mapState in the Redux store.
 * @param state - The state of the redux store
 */
export const selectMapResults = (state) => selectMapDataState(state)?.results;

/**
 * It returns the Geo Coordinates from the mapState in the Redux store.
 * Memoized so distinctUntilChanged() comparisons by reference are stable.
 * @param state - The state of the redux store
 */
export const selectGeoCoordinates = createSelector(
  selectMapDataState,
  (mapData) => mapData?.geoCoordinates,
);

/*
* Assumes any non-null geoCoordinates value represents valid, usable boundaries.
* Coordinate validity is enforced at the dispatch site (map.plugin).
*/
export const areMapBoundariesDefined = (state) => !!selectGeoCoordinates(state);

/**
 * Returns an array of site plans from the mapState in the Redux store.
 * @param state - The state of the redux store
 */
export const selectSitePlans = (state) => selectMapDataState(state)?.sitePlans;

/**
 * Returns site plans keyed by SitePlanId for O(1) lookup.
 *
 * Memoized by the site plans array reference, ensuring stable references
 * for downstream distinctUntilChanged() comparisons and render optimizations.
 *
 * @param {object} state - Redux store state
 * @returns {Object<string, object>} Site plans indexed by SitePlanId
 */
export const selectSitePlansById = createSelector(
  selectSitePlans,
  (plans) => normalizeBySitePlanId(plans),
);

/**
 * Returns the fetch results status from the mapState in the Redux store.
 * @param state - The state of the redux store
 */
export const selectFetchResultsStatus = (state) => selectMapDataState(state)?.fetchResultsStatus;

/**
 * Returns the selected marker from the mapInteractionState in the Redux store.
 * @param state - The state of the redux store
 */
export const selectSelectedMarker = (state) => selectMapInteractionState(state)?.selectedMarker;

/**
 * Returns a memoized Set containing all communityIds currently present
 * in the map results.
 *
 * Useful for O(1) membership checks when rendering or filtering map-related UI.
 *
 * The Set is recreated only when the map results array reference changes.
 *
 * @param {object} state - Redux store state
 * @returns {Set<number>} Memoized set of communityIds
 */
export const selectMapResultCommunityIds = createSelector(
  selectMapResults,
  (results) => new Set(
    results.map(({ communityId }) => communityId),
  ),
);

/**
 * Returns the marker cards from the mapInteractionState in the Redux store.
 * @param state - The state of the redux store
 */
export const selectMarkerCards = (state) => selectMapInteractionState(state)?.markerCards;

/**
 * Returns the site plan cards from the mapInteractionState in the Redux store.
 * @param state - The state of the redux store
 */
export const selectSitePlanCards = (state) => selectMapInteractionState(state)?.sitePlanCards;
